import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabaseClient } from '@/lib/supabase/client'
import type { User } from '@supabase/supabase-js'

interface AuthUser {
  id: string
  username: string
  display_name: string
  avatar_url: string | null
  is_verified: boolean
  karma_score: number
  role: string
  email: string
}

interface AuthContextValue {
  user: AuthUser | null
  supabaseUser: User | null
  loading: boolean
  initialized: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  supabaseUser: null,
  loading: true,
  initialized: false,
  signOut: async () => {},
  refreshUser: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

async function fetchProfile(userId: string): Promise<AuthUser | null> {
  const { data, error } = await supabaseClient
    .from('users')
    .select('id, username, display_name, avatar_url, is_verified, karma_score, role, email')
    .eq('id', userId)
    .single()
  if (error || !data) return null
  return data as AuthUser
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const navigate = useNavigate()

  const refreshUser = async () => {
    const { data: { user: sbUser } } = await supabaseClient.auth.getUser()
    if (sbUser) {
      const profile = await fetchProfile(sbUser.id)
      setUser(profile)
      setSupabaseUser(sbUser)
    } else {
      setUser(null)
      setSupabaseUser(null)
    }
  }

  useEffect(() => {
    // Initial session
    supabaseClient.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setSupabaseUser(session.user)
        const profile = await fetchProfile(session.user.id)
        setUser(profile)
      }
      setLoading(false)
      setInitialized(true)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setSupabaseUser(null)
        navigate('/auth/login')
      } else if (session?.user) {
        setSupabaseUser(session.user)
        const profile = await fetchProfile(session.user.id)
        setUser(profile)
      }
      setLoading(false)
      setInitialized(true)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    await supabaseClient.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, supabaseUser, loading, initialized, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}
